import React, { Suspense, useEffect } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { CSpinner, useColorModes } from "@coreui/react-pro";

import "./scss/style.scss";
import "./scss/examples.scss";

import type { State } from "./store";
import { useAuth } from "./providers/AuthProvider";

import routes from "./routes";

const Login = React.lazy(() => import("./views/login/Login"));
const LoginWithCode = React.lazy(() => import("./views/login/LoginWithCode"));
const DefaultLayout = React.lazy(() => import("./layout/DefaultLayout"));

const loadingSpinner = (
  <div className="pt-3 text-center">
    <CSpinner color="primary" variant="grow" />
  </div>
);

const App = () => {
  const { isColorModeSet, setColorMode } = useColorModes(
    "coreui-pro-react-admin-template-theme-modern"
  );
  const storedTheme = useSelector((state: State) => state.theme);
  const { token, user, loading } = useAuth();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.href.split("?")[1]);
    let theme = urlParams.get("theme");

    if (theme !== null && theme.match(/^[A-Za-z0-9\s]+/)) {
      theme = theme.match(/^[A-Za-z0-9\s]+/)![0];
    }

    if (theme) {
      setColorMode(theme);
    }

    if (isColorModeSet()) {
      return;
    }

    setColorMode(storedTheme);
  }, []);

  if (loading) return loadingSpinner;

  return (
    <Suspense fallback={loadingSpinner}>
      <Routes>
        {/* public login routes */}
        <Route
          path="/login"
          element={token && user ? <Navigate to="/dashboard" /> : <Login />}
        />
        <Route
          path="/login-with-code/:code"
          element={<LoginWithCode />}
        />

        {/* app routes from your routes.tsx */}
        {routes.map((r) =>
          r.path && r.element ? (
            <Route
              key={r.path}
              path={r.path}
              element={
                token && user ? (
                  <DefaultLayout hideSidebar={r.hideSidebar} fullWidth={r.fullWidth}>
                    <Suspense fallback={loadingSpinner}>
                      <r.element />
                    </Suspense>
                  </DefaultLayout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
          ) : null
        )}

        {/* catch-all */}
        <Route
          path="*"
          element={
            token && user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </Suspense>
  );
};

export default App;
